import { Component, Inject, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete'

import { forkJoin, Observable, Subject } from 'rxjs'

import { KeyNodeTitle, KeyNodeType, Keyway } from '@overtur-monorepo/keying-shared'
import { MasterKeyService } from '../../services/keying.masterkey.service'
import { filter, map, startWith, take, takeUntil, throttleTime } from 'rxjs/operators'
import { KeywayService, MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { KeywayType } from '@overtur-monorepo/keying-shared'
import { Book } from '../../models/dev.test.models'
import { KeyingLibraryService } from '../../services/keying.library.service'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { DialogService } from '../../services/dialog.service'
import { KeyingBookService } from '../../services/keying.book.service'
import { ErrorService } from '../../services/error.service'
import { KeyingDataSetsService } from '../../services/keying.data.sets.service'
import { LoadingService } from '../../services/loading.service'
import { KeyingSystemService } from '../../services/keying.system.service'
import { LoggingService } from '../../services/logging.service'

@Component({
    selector: 'app-move-keys-dialog',
    templateUrl: './move-keys-dialog.component.html',
    styleUrls: ['./move-keys-dialog.component.scss'],
    standalone: false
})
export class MoveKeysDialogComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('autoMasterKey') autoMasterKey: MatAutocomplete
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger
  destroy$ = new Subject()
  keyNodeType = KeyNodeType
  moveForm: FormGroup
  isSelectingFromAutocomplete = false

  selectiveData: any
  lasSKD
  formData
  showConvert = true

  skipErrors = false
  max = 1

  focus = {
    keySetSymbols: false,
    prefix: false,
    from: false,
    to: false,
    suffix: false,
    targetKeySystemName: false,
    masterKeySetSymbol: false,
    tmkBitting: false,
    keySectionId: false,
  }

  selectedKey
  defaultIndSystem
  indSystemList = []
  familyKeywayList = []
  keySectionList = []
  parentKeywayList = []
  uniqueParentKeyways = []
  selectedIndSystem
  selectedIndSystemName
  masterKeyMoveList = []
  masterKeyList = []
  filteredMasterKeys$: Observable<any[]>
  userInputtedSymbols
  assignedKeySymbolList: string[] = []
  otherAssignedKeySymbolList: string[] = []

  defaultKeySection: Keyway
  selectedBook: Book
  bookKeyway$ = new Subject<Keyway>()

  public errorMessages = {
    keySetSymbols: [{ type: 'required', message: 'Please enter one or multiple key(s) to move.' }],
    prefix: [
      { type: 'required', message: 'Prefix is required.' },
      { type: 'beginsWithX', message: 'Can not start with X.' },
    ],
    from: [{ type: 'required', message: 'From is required.' }],
    to: [{ type: 'required', message: 'To is required.' }],
    suffix: [{ type: 'required', message: 'Suffix is required.' }],
    targetKeySystemName: [{ type: 'required', message: 'Please choose an Independent System.' }],
    masterKeySetSymbol: [
      { type: 'required', message: 'Please choose where to move key.' },
      { type: 'invalidSelection', message: 'No keys found.' }
    ],
    tmkBitting: [{ type: 'required', message: 'Please assign a TMK Combination.' }],
    keySectionId: [{ type: 'required', message: 'Please assign a Key Section.' }],
  }

  prefix = ''

  constructor(
    public form: FormBuilder,
    private dialogRef: MatDialogRef<MoveKeysDialogComponent>,
    private dialogService: DialogService,
    private masterKeyService: MasterKeyService,
    private keywayService: KeywayService,
    private keyingLibraryService: KeyingLibraryService,
    private keyingBookService: KeyingBookService,
    private errorService: ErrorService,
    private keyingDataSetsService: KeyingDataSetsService,
    private keyingSystemService: KeyingSystemService,
    private loadingService: LoadingService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loggingService: LoggingService
  ) {
    this.selectiveData = data
    this.selectedKey = this.selectiveData?.masterKey.keySetSymbol
    this.defaultIndSystem = this.selectiveData?.keySystem
    this.selectedIndSystem = this.selectiveData?.keySystem
    if (this.validate()) {
      this.setPrefix()
      this.createForm()
    }
  }

  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  ngOnInit() {
    //When initializing the component, get the updated keysystems within the structures
    this.keyingSystemService
      .getKeySystems(this.selectiveData.structure.id)
      .pipe(take(1))
      .subscribe((systems) => {
        this.indSystemList = systems
        this.selectedIndSystem = this.indSystemList.find((system) => system.id === this.selectedIndSystem.id)
        this.selectedIndSystemName = this.selectedIndSystem.topMasterKeyName
        this.moveForm.patchValue({ targetKeySystemName: this.selectedIndSystem })
      })
    this.userInputtedSymbols = []
    switch (this.selectiveData.action) {
      case KeyNodeTitle.MOVE_KEYS:
        this.max = 5
        break
    }
    if (
      this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.ChangeKeyThruNode ||
      this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.SingleKeyDifferentThruNode
    ) {
      this.populateThruNotation(this.selectiveData.masterKey)
    }
    //For Falcon SFIC A4 combinating Rule only
    //You can move the Control Node to directly under TMK if a new TMK has been created
    else if (
      this.selectiveData.structure.combinatingRuleSetId === 7 &&
      (this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.ControlKeyMaster ||
        this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.ConstructionKeyMaster ||
        this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.SingleKeyDifferentMaster ||
        this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.DirectChangeMaster ||
        this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.CrossKeyMaster ||
        this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.OrphanMaster)
    ) {
      this.moveForm.controls.keySetSymbols.patchValue(this.selectiveData.masterKey.keySetSymbol)
      this.moveForm.controls.masterKeySetSymbol.patchValue(this.selectiveData.keySystem.topMasterKeyName)
      this.disableAllFormFields()
    } else {
      this.moveForm.patchValue({ keySetSymbols: this.selectiveData.masterKey.keySetSymbol })
    }
    this.getMasterKeysToMove()
    this.getMoveToMasterKeys()
    this.loadKeySections()
    this.setupMasterKeyAutocomplete()
  }

  /* istanbul ignore next */
  setupMasterKeyAutocomplete() {
    this.filteredMasterKeys$ = this.moveForm.controls.masterKeySetSymbol.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterMasterKeys(value || ''))
    )
  }

  /* istanbul ignore next */
  private _filterMasterKeys(value: string): any[] {
    const filterValue = typeof value === 'string' ? value.trim().toLowerCase() : ''

    // If no filter value, return all options
    if (!filterValue) {
      const baseOptions = this.showConvert ? [{ keySetSymbol: 'convert to tmk', isSpecial: true }] : []
      return [...baseOptions, ...this.masterKeyMoveList]
    }

    // Start with base options - only include if "convert to tmk" starts with filter value
    const baseOptions = this.showConvert && 'convert to tmk'.startsWith(filterValue)
      ? [{ keySetSymbol: 'convert to tmk', isSpecial: true }]
      : []

    // Filter master keys - only show if they start with the typed letter
    const filteredKeys = this.masterKeyMoveList.filter(option =>
      option.keySetSymbol?.toLowerCase().startsWith(filterValue)
    )

    return [...baseOptions, ...filteredKeys]
  }

  /* istanbul ignore next */
  displayMasterKey(option: any): string {
    return option && option.keySetSymbol ? option.keySetSymbol : option
  }

  /* istanbul ignore next */
  ngOnChanges() {
    this.getMoveToMasterKeys()
  }

  validate() {
    if (!this.selectiveData) return false
    if (!this.selectiveData.action) return false
    return true
  }

  /* istanbul ignore next */
  setPrefix(): void {
    this.prefix = ''
  }

  /* istanbul ignore next */
  createForm(): void {
    this.moveForm = this.form.group({
      keySetSymbols: new FormControl(''),
      prefix: new FormControl(
        {
          value: this.prefix,
          disabled: this.prefix === KeyNodeTitle.SKD,
        },
        [this.beginsWithXValidator]
      ),
      from: new FormControl(''),
      to: new FormControl(''),
      suffix: new FormControl({
        value: '',
        disabled: this.prefix === KeyNodeTitle.SKD,
      }),
      targetKeySystemName: new FormControl('', [Validators.required]),
      masterKeySetSymbol: new FormControl('', [Validators.required, this.validMasterKeySelectionValidator.bind(this)]),
      tmkBitting: new FormControl(''),
      keySectionId: new FormControl(''),
    })
  }

  beginsWithXValidator = (control: FormControl) => {
    let value = control.value
    if (value !== '') {
      const beginsWithX = value.charAt(0).toUpperCase() === 'X'
      if (beginsWithX) {
        return { beginsWithX, type: 'beginsWithX' }
      }
    }
    return null
  }

  validMasterKeySelectionValidator = (control: FormControl) => {
    const value = control.value?.trim()

    // Empty value is handled by required validator
    if (!value || value === '') {
      return null
    }

    // Check if value is the special "convert to tmk" option (case-insensitive)
    if (value.toLowerCase() === 'convert to tmk') {
      return null
    }

    // Check if value exactly matches a key in the masterKeyMoveList (case-insensitive)
    const isValidSelection = this.masterKeyMoveList.some(
      (key) => key.keySetSymbol?.toLowerCase() === value.toLowerCase()
    )

    if (isValidSelection) {
      return null
    }

    // Not an exact match - check if there are any partial matches
    const filterValue = typeof value === 'string' ? value.toLowerCase() : ''

    // Check if "convert to tmk" starts with the typed value
    const hasConvertOption = this.showConvert && 'convert to tmk'.startsWith(filterValue)

    // Check if any keys start with what the user typed
    const hasMatchingKeys = this.masterKeyMoveList.some(
      (key) => key.keySetSymbol?.toLowerCase().startsWith(filterValue)
    )

    if (hasConvertOption || hasMatchingKeys) {
      // User typed partial text but options are available in dropdown
      // Return error to keep form invalid, but use a different error type
      // so we don't show "No keys found" message
      return { partialInput: true }
    }

    // No matching keys at all - show error message
    return { invalidSelection: true }
  }

  /* istanbul ignore next */
  onMasterKeyInput() {
    // Trigger validation when user types
    const control = this.moveForm.get('masterKeySetSymbol')
    control.markAsTouched()
    control.markAsDirty()
    control.updateValueAndValidity()
  }

  /* istanbul ignore next */
  onMasterKeySelected() {
    // Set flag to prevent immediate form submission
    this.isSelectingFromAutocomplete = true

    // When user selects a valid option from autocomplete
    const control = this.moveForm.get('masterKeySetSymbol')

    // Trim the value
    const trimmedValue = control.value?.trim()
    if (trimmedValue !== control.value) {
      control.setValue(trimmedValue, { emitEvent: false })
    }

    // Update validation state
    control.updateValueAndValidity()
    control.markAsTouched()
    control.markAsDirty()

    this.checkFormRequirements()

    // Reset flag after a short delay to allow for next interaction
    setTimeout(() => {
      this.isSelectingFromAutocomplete = false
    }, 100)
  }

  /* istanbul ignore next */
  onOptionActivated(event: any) {
    // When user navigates with arrow keys, update the input to show the highlighted option
    if (event.option) {
      const highlightedValue = event.option.value
      this.moveForm.get('masterKeySetSymbol').setValue(highlightedValue, { emitEvent: false })
    }
  }

  /* istanbul ignore next */
  validateMasterKeySelection() {
    // This runs on blur - trim the value and trigger validation
    const control = this.moveForm.get('masterKeySetSymbol')
    const trimmedValue = control.value?.trim()
    if (trimmedValue !== control.value) {
      control.setValue(trimmedValue, { emitEvent: false })
    }
    control.updateValueAndValidity()
    control.markAsTouched()
  }

  /* istanbul ignore next */
  onMasterKeyKeydown(event: KeyboardEvent) {
    // Prevent space if the field is empty or contains only spaces
    if (event.key === ' ' || event.code === 'Space') {
      const currentValue = this.moveForm.get('masterKeySetSymbol').value || ''
      const trimmedValue = currentValue.trim()

      // If field is empty or only has spaces, prevent space from being entered
      if (trimmedValue.length === 0) {
        event.preventDefault()
        return
      }
      // Otherwise allow space (field has at least one non-space character)
    }

    // If Enter key is pressed and panel is open
    if (event.key === 'Enter' && this.autocompleteTrigger && this.autocompleteTrigger.panelOpen) {
      // Set flag to prevent form submission
      this.isSelectingFromAutocomplete = true

      // Always prevent default and stop propagation to prevent form submission when panel is open
      event.preventDefault()
      event.stopPropagation()

      const currentValue = this.moveForm.get('masterKeySetSymbol').value?.trim()

      // Check if there's an exact match (case-insensitive)
      const exactMatch = this.masterKeyMoveList.find(
        (key) => key.keySetSymbol?.toLowerCase() === currentValue?.toLowerCase()
      )

      // Also check for "convert to tmk" special option
      const isConvertMatch = this.showConvert && currentValue?.toLowerCase() === 'convert to tmk'

      if (exactMatch || isConvertMatch) {
        // Exact match found - manually close the autocomplete panel
        // Update the form value with trimmed version
        this.moveForm.get('masterKeySetSymbol').setValue(currentValue, { emitEvent: false })

        // Close panel using the trigger
        this.autocompleteTrigger.closePanel()

        // Trigger validation
        const control = this.moveForm.get('masterKeySetSymbol')
        control.updateValueAndValidity()
        control.markAsTouched()
        control.markAsDirty()

        this.checkFormRequirements()
      }

      // Reset flag after a short delay to allow for next interaction
      setTimeout(() => {
        this.isSelectingFromAutocomplete = false
      }, 100)

      // For non-exact matches (keyboard navigation), let Material autocomplete handle the selection
      // The preventDefault and stopPropagation above ensure the form won't submit
      return
    }
  }

  /* istanbul ignore next */
  onFormEnter(event: any) {
    // Prevent default form submission behavior
    event.preventDefault()

    // If autocomplete panel is open, don't submit - let Material handle selection
    if (this.autocompleteTrigger && this.autocompleteTrigger.panelOpen) {
      return
    }

    // If user just selected from autocomplete, don't submit
    if (this.isSelectingFromAutocomplete) {
      return
    }

    // Only submit if form is valid
    if (this.moveForm.valid) {
      this.checkAssignedKeyCombinations()
    }
  }

  /*istanbul ignore next */
  populateThruNotation(masterKey: MasterKey) {
    // Populates the Move Keys Form onInit
    if (masterKey.keySetSymbol.includes('-')) {
      this.moveForm.patchValue({ prefix: masterKey.prefix})
      this.moveForm.patchValue({ from: masterKey.from })
      this.moveForm.patchValue({ to: masterKey.to })
      this.moveForm.patchValue({ suffix: masterKey.suffix })
  } else {
      this.moveForm.patchValue({ keySetSymbols: masterKey.keySetSymbol });
  }
  }

  /* istanbul ignore next */
  loadKeySections() {
    const keywayFamilyId = this.selectiveData.structure.keywayFamilyId
    // const keySectionId = this.selectiveData.masterKey.keywayId

    this.keywayService
      .getAllKeyways(keywayFamilyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((keyway) => {
        this.familyKeywayList = keyway
        this.familyKeywayList.push(keyway)
      })
  }

/* istanbul ignore next */
  getParentKeyways() {
    this.keySectionList = []
    const selectedIndSystemTMK = this.masterKeyMoveList.find((master) => master.distanceFromTMK === 0)
    const tmkKeywayId = selectedIndSystemTMK.keywayId
    let tmkKeyway = this.familyKeywayList.find((k) => {
      return k.id === tmkKeywayId
    })
    //Check if parent keyways exist on current tmk keyway
    if (tmkKeyway.parentKeyways.length > 0) {
      // //Add Parent Keyways to the list within the form dropdown
      this.keyingDataSetsService.getParentKeyWays(tmkKeyway.keywayFamilyId, tmkKeyway.id).subscribe((res) => {
        this.keySectionList = res
        // //Always include the current tmk keyway level as an option
        this.keySectionList.push(tmkKeyway)
      })
    } else {
      // //Always include the current tmk keyway level as an option
      this.keySectionList.push(tmkKeyway)
    }
  }

  /* istanbul ignore next */
  removeDuplicateParentKeyways(parentArray) {
    return parentArray.filter((obj, index, self) => index === self.findIndex((t) => t.id === obj.id))
  }

  checkAssignedKeyCombinations() {
    if (this.selectiveData.masterKeyTypeId !== this.keyNodeType.ControlKeyMaster) {
      this.userInputtedSymbols = []
      // Empty the global variable arrays
      this.assignedKeySymbolList = []
      this.otherAssignedKeySymbolList = []
      this.setThruNotation()
      this.loadingService.set({ isLoading: true, message: 'Checking for Key Assignments' })
      // Check if any inputted keySetSymbols are assigned
      if (this.moveForm.controls.keySetSymbols.value !== '') {
        this.userInputtedSymbols = [
          ...this.userInputtedSymbols,
          this.moveForm.controls.keySetSymbols.value.toUpperCase().split(','),
        ]
      }

      this.organizeAllCombos()
    } else {
      this.organizeAllCombos()
    }
  }

  setThruNotation() {
    // Checks User Inputs for updated values
    if (this.moveForm.controls.from.value !== '') {
      const thruPrefix = this.moveForm.controls.prefix.value
      const thruFrom = this.moveForm.controls.from.value
      const thruTo = this.moveForm.controls.to.value
      const thruSuffix = this.moveForm.controls.suffix.value
      for (let i = thruFrom; i <= thruTo; i++) {
        this.userInputtedSymbols.push(thruPrefix.toUpperCase() + i + thruSuffix.toUpperCase())
      }
    }
  }

  organizeAllCombos() {
    if (this.selectiveData.masterKey.masterKeyTypeId !== this.keyNodeType.ControlKeyMaster) {
      let checkMasterKeyList = this.masterKeyList.filter((key) =>
        this.userInputtedSymbols.flat().includes(key.keySetSymbol)
      )

      let myCalls = []
      // get unique parentIds for the user inputted keys
      // dont want to loop through the same parent multiple times, so removing duplications
      let uniqueParentIds = checkMasterKeyList.map((p) => p.parentId)
      uniqueParentIds = [...new Set(uniqueParentIds)]
      // for each unique parent id, get books
      uniqueParentIds.forEach((parentId) => {
        myCalls.push(this.keyingBookService.getCombinations(this.selectiveData.keySystem.id, parentId))
      })

      forkJoin(myCalls)
        .pipe(take(1))
        .subscribe((resData) => {
          resData.forEach((books) => {
            if (books.length) {
              // Check books for any user inputted keySymbols that are assigned

              books.forEach((book) => {
                let assignedBittings = book.bittings.filter((x) => x.assignedMasterKeyId)
                let bookMatchFound = false

                let otherAssignedBittings = []
                let tempList = []
                // check all books with assignment for matching key set symbols to user inputs
                assignedBittings.forEach((y) => {
                  let matchFound = false

                  this.userInputtedSymbols.flat().forEach((keySetSymbol) => {
                    if (y.keySetSymbol === keySetSymbol) {
                      matchFound = true
                      bookMatchFound = true
                    }
                  })

                  if (!matchFound) {
                    tempList.push(y)
                  }
                })

                if (bookMatchFound) {
                  tempList.forEach((f) => otherAssignedBittings.push(f))
                }

                let notUserInputtedSymbols = otherAssignedBittings.map((kss) => (kss = kss.keySetSymbol))
                notUserInputtedSymbols.forEach((jig) => {
                  this.otherAssignedKeySymbolList.push(jig)
                })
              })
            }
            this.assignedKeySymbolList = checkMasterKeyList.map((k) => k.keySetSymbol)
          })
          this.loadingService.set({ isLoading: false, message: '' })
          this.submit()
        })
    } else {
      this.assignedKeySymbolList = this.selectiveData.masterKey.keySetSymbol
      this.submit()
    }
  }

  setFormIndSystem() {
    if (
      this.moveForm.controls.targetKeySystemName.value === 'New' ||
      this.moveForm.controls.targetKeySystemName.value === ''
    ) {
      this.selectedIndSystemName = this.moveForm.controls.targetKeySystemName.value
    } else {
      this.selectedIndSystemName = this.moveForm.controls.targetKeySystemName.value.topMasterKeyName
    }
    this.masterKeyMoveList = []
    this.moveForm.patchValue({ masterKeySetSymbol: '' })
    this.moveForm.patchValue({ keySectionId: '' })
    this.moveForm.patchValue({ tmkBitting: '' })
    this.moveForm.patchValue({ targetKeySystemName: this.selectedIndSystemName })
    if (this.selectedIndSystem === 'New' || this.selectedIndSystem === '') {
      this.moveForm.patchValue({ masterKeySetSymbol: 'convert to tmk' })
      this.masterKeyMoveList = []
    } else {
      this.getMoveToMasterKeys()
    }
  }

   /* istanbul ignore next */
  //get all keys within system that you wish to move to
  getMoveToMasterKeys() {
    if (
      this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.SelectiveMaster ||
      this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.Direct ||
      this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.SingleKeyDifferent
    ) {
      this.showConvert = false
    } else {
      this.showConvert = true
    }
    this.masterKeyMoveList = []
    this.masterKeyService
      .getMasterKeys(this.selectiveData.structure.id, this.selectedIndSystem.id, true)
      .pipe(take(1))
      .subscribe((keys: MasterKey) => {
        this.masterKeyMoveList = keys
          .squashIncludeChangeKeys()
          .filter(
            (key) =>
              key.masterKeyTypeId === this.keyNodeType.Master ||
              key.masterKeyTypeId === this.keyNodeType.SingleKeyDifferentMaster ||
              key.masterKeyTypeId === this.keyNodeType.DirectChangeMaster
          )
        // Re-trigger autocomplete filtering when list updates
        this.setupMasterKeyAutocomplete()
      })
  }

  /* istanbul ignore next */
  //get all eligible keys that can be moved within system
  getMasterKeysToMove() {
    this.keyingDataSetsService.keySystemDataSets$.pipe(take(1)).subscribe((key) => {
      const keys = key.masterKeysKeying
      this.loggingService.log('Keys:', keys)
      this.masterKeyList = keys.squashIncludeChangeKeys()
      this.loggingService.log('Masterkey List:', this.masterKeyList)
      this.loggingService.log(
        'changeKeys',
        this.masterKeyList.filter((k) => k.masterKeyTypeId === this.keyNodeType.ChangeKey)
      )
      this.loggingService.log(
        'masterkey',
        this.masterKeyList.filter((k) => k.masterKeyTypeId === this.keyNodeType.Master)
      )
    })
  }

  /* istanbul ignore next */
  submit() {
    //If Assigned include popup of other keys assigned that will be moved
    if (this.moveForm.valid) {
      if (this.selectiveData.masterKey.masterKeyTypeId !== this.keyNodeType.ControlKeyMaster) {
        this.formData = {
          keySetSymbols: this.assignedKeySymbolList.toString(),
          action: this.selectiveData.action,
          prefix: this.moveForm.controls.prefix.value,
          from: this.moveForm.controls.from.value,
          to: this.moveForm.controls.to.value,
          suffix: this.moveForm.controls.suffix.value,
          targetKeySystemName: this.selectedIndSystemName,
          masterKeySetSymbol: this.moveForm.controls.masterKeySetSymbol.value,
          keySectionId: this.moveForm.controls.keySectionId.value.id,
          tmkBitting: this.moveForm.controls.tmkBitting.value,
          masterKeyId: this.selectiveData.masterKey.id,
        }
      } else {
        this.formData = {
          keySetSymbols: this.selectiveData.masterKey.keySetSymbol,
          action: this.selectiveData.action,
          prefix: this.moveForm.controls.prefix.value,
          from: this.moveForm.controls.from.value,
          to: this.moveForm.controls.to.value,
          suffix: this.moveForm.controls.suffix.value,
          targetKeySystemName: this.selectedIndSystemName,
          masterKeySetSymbol: this.moveForm.controls.masterKeySetSymbol.value,
          keySectionId: this.moveForm.controls.keySectionId.value.id,
          tmkBitting: this.moveForm.controls.tmkBitting.value,
          masterKeyId: this.selectiveData.masterKey.id,
        }
      }

      if (this.otherAssignedKeySymbolList.length > 0) {
        const confirmData = {
          message: `Additional assigned keys ${this.otherAssignedKeySymbolList} have been found on the book(s) and will be moved with the requested keys.`,
          showCancel: true,
          cancel: 'Cancel',
          confirm: 'Confirm',
        }
        const dialog = this.dialogService.open(ConfirmDialogComponent, confirmData, false)
        dialog
          .afterClosed()
          .pipe(take(1))
          .subscribe((result) => {
            if (result.confirmed) {
              this.formData.keySetSymbols =
                this.assignedKeySymbolList.toString() + ',' + this.otherAssignedKeySymbolList.toString()
              this.dialogRef.close(this.formData)
              this.errorService.confirm('Book has been moved.')
            }
          })
      } else {
        this.formData.keySetSymbols = this.assignedKeySymbolList.toString()
        this.dialogRef.close(this.formData)
      }
    }
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasInvalidSelectionError(form, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.hasError('invalidSelection') && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  close() {
    this.dialogRef.close()
  }

  /* istanbul ignore next */
  checkFormRequirements() {
    if (
      this.moveForm.controls.targetKeySystemName.value !== 'New' &&
      this.moveForm.controls.masterKeySetSymbol.value === 'convert to tmk'
    ) {
      this.getParentKeyways()
      this.moveForm.get('tmkBitting').setValidators([Validators.required])
      this.moveForm.get('tmkBitting').updateValueAndValidity()
      this.moveForm.get('keySectionId').setValidators([Validators.required])
      this.moveForm.get('keySectionId').updateValueAndValidity()
    } else {
      this.moveForm.get('tmkBitting').clearValidators()
      this.moveForm.get('keySectionId').clearValidators()
      this.moveForm.get('tmkBitting').updateValueAndValidity()
      this.moveForm.get('keySectionId').updateValueAndValidity()
    }
  }

  /* istanbul ignore next */
  disableAllFormFields() {
    this.moveForm.controls.keySetSymbols.disable()
    this.moveForm.controls.prefix.disable()
    this.moveForm.controls.from.disable()
    this.moveForm.controls.to.disable()
    this.moveForm.controls.suffix.disable()
    this.moveForm.controls.targetKeySystemName.disable()
    this.moveForm.controls.masterKeySetSymbol.disable()
  }
}
